import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Check, FileText } from '@phosphor-icons/react';
import axios from 'axios';
import { API } from '../../App';

const Pages = () => {
  const [selectedPage, setSelectedPage] = useState('brand-story');
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { register, handleSubmit, reset, setValue } = useForm();

  const pages = [
    { id: 'brand-story', name: 'Notre Histoire' },
    { id: 'contact', name: 'Contact' }
  ];

  useEffect(() => {
    fetchPageContent();
  }, [selectedPage]);

  const fetchPageContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pages/${selectedPage}`);
      setPageContent(response.data);
      
      // Set form values
      if (response.data.sections) {
        Object.keys(response.data.sections).forEach(key => {
          setValue(`sections.${key}`, response.data.sections[key]);
        });
      }
    } catch (error) {
      console.error('Failed to fetch page content:', error);
      setPageContent({ page_name: selectedPage, sections: {} });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setSaved(false);

    try {
      await axios.put(`${API}/admin/pages/${selectedPage}`, {
        sections: data.sections || {}
      }, {
        withCredentials: true
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save page content:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const renderPageForm = () => {
    switch (selectedPage) {
      case 'brand-story':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-stone-400 mb-2">Titre Principal</label>
              <input
                type="text"
                className="admin-input"
                placeholder="The Art of Event Bars"
                {...register('sections.hero_title')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Introduction - Titre</label>
              <input
                type="text"
                className="admin-input"
                placeholder="La Naissance d'une Vision"
                {...register('sections.intro_title')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Introduction - Paragraphe 1</label>
              <textarea
                rows={4}
                className="admin-input resize-none"
                {...register('sections.intro_p1')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Introduction - Paragraphe 2</label>
              <textarea
                rows={4}
                className="admin-input resize-none"
                {...register('sections.intro_p2')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Introduction - Paragraphe 3</label>
              <textarea
                rows={4}
                className="admin-input resize-none"
                {...register('sections.intro_p3')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Philosophie - Titre</label>
              <input
                type="text"
                className="admin-input"
                placeholder="Notre Philosophie"
                {...register('sections.philosophy_title')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Philosophie - Paragraphe 1</label>
              <textarea
                rows={4}
                className="admin-input resize-none"
                {...register('sections.philosophy_p1')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Philosophie - Paragraphe 2</label>
              <textarea
                rows={4}
                className="admin-input resize-none"
                {...register('sections.philosophy_p2')}
              />
            </div>
          </div>
        );
      
      case 'contact':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-stone-400 mb-2">Titre de la Page</label>
              <input
                type="text"
                className="admin-input"
                placeholder="Demander un Concept"
                {...register('sections.title')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Description</label>
              <textarea
                rows={3}
                className="admin-input resize-none"
                {...register('sections.description')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Email de Contact</label>
              <input
                type="email"
                className="admin-input"
                placeholder="contact@studiobar.com"
                {...register('sections.contact_email')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Téléphone</label>
              <input
                type="tel"
                className="admin-input"
                placeholder="+33 1 23 45 67 89"
                {...register('sections.phone')}
              />
            </div>
            <div>
              <label className="block text-sm text-stone-400 mb-2">Adresse</label>
              <input
                type="text"
                className="admin-input"
                placeholder="Paris, France"
                {...register('sections.address')}
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div data-testid="admin-pages">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-white mb-2">Pages</h1>
        <p className="text-stone-400">Modifiez le contenu des pages du site</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Page List */}
        <div className="lg:col-span-1">
          <div className="admin-card divide-y divide-white/5">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => {
                  setSelectedPage(page.id);
                  reset();
                }}
                className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors ${
                  selectedPage === page.id
                    ? 'bg-[#C5A059]/10 text-[#C5A059]'
                    : 'text-stone-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <FileText size={18} weight={selectedPage === page.id ? 'fill' : 'light'} />
                <span className="text-sm">{page.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Page Editor */}
        <div className="lg:col-span-3">
          <div className="admin-card p-6">
            <h2 className="font-display text-xl text-white mb-6">
              {pages.find(p => p.id === selectedPage)?.name}
            </h2>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {renderPageForm()}

                <div className="flex items-center gap-4 mt-8 pt-6 border-t border-white/5">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Check size={18} weight="bold" />
                        Sauvegarder
                      </>
                    )}
                  </button>
                  {saved && (
                    <span className="text-green-400 text-sm">Contenu sauvegardé !</span>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pages;
